/**
 * Обзор (docs/08-screens.md).
 *
 * Главный экран отвечает на четыре вопроса без прокрутки на ноутбуке:
 * на сколько хватит денег, куда они уходят, что горит по срокам,
 * кто чем занят. Ничего не кликаем, если всё зелёное.
 */
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Icon } from '@/ds';
import { EmptyState } from '@/components/EmptyState';
import { BarChart, Eyebrow, Money, Provenance } from '@/components/money';
import { useOverview, type OverviewService } from '@/api/hooks';
import { formatDate, formatDateShort, formatMoney, formatSignedPercent, plural } from '@/lib/format';
import { BILLING_LABELS, forecastText } from './FinanceScreen';
import { QueryState } from '@/components/QueryState';

/** Иконка провайдера. Только Lucide: собственных логотипов в системе нет. */
const PROVIDER_ICONS: Record<string, string> = {
  openai: 'AudioLines',
  anthropic: 'Sparkles',
  digitalocean: 'Server',
  google_workspace: 'Mail',
  manual: 'Box',
};

/**
 * Тревога передаётся цветом числа и подписи, не заливкой блока
 * и не цветной полосой слева (docs/10-design.md).
 */
function toneFor(service: OverviewService): 'normal' | 'warning' | 'danger' {
  const worst = service.triggered[0];
  if (!worst) return 'normal';
  return worst.level === 'critical' ? 'danger' : 'warning';
}

function BalanceCard({ service, onOpen }: { service: OverviewService; onOpen: () => void }) {
  const tone = toneFor(service);
  const warning = service.triggered[0];

  return (
    <Card padding="md" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <span
          style={{
            width: 28,
            height: 28,
            flex: '0 0 28px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--surface-brand-soft)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icon
            name={PROVIDER_ICONS[service.provider] ?? 'Box'}
            size={16}
            color="var(--burgundy-700)"
          />
        </span>
        <span
          style={{
            font: '500 14px var(--font-text)',
            color: 'var(--text-strong)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {service.name}
        </span>
      </div>

      <div>
        {service.billingModel === 'prepaid_balance' ? (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
              <Money minor={service.balanceMinor} currency={service.currency} size="lg" tone={tone} />
              <span style={{ font: '400 11px var(--font-text)', color: 'var(--text-subtle)' }}>
                расчётный
              </span>
            </div>
            <Provenance>
              <span style={{ color: tone === 'normal' ? undefined : 'var(--status-warning-fg)' }}>
                {forecastText(service)}
              </span>
            </Provenance>
          </>
        ) : service.billingModel === 'subscription' && service.subscription ? (
          <>
            <Money minor={service.subscription.amount} currency={service.currency} size="lg" />
            <Provenance>
              {`Списание ${formatDate(service.subscription.nextChargeAt)}`}
            </Provenance>
          </>
        ) : service.billingModel === 'postpaid_invoice' ? (
          <>
            <Money minor={service.providerPendingMinor} currency={service.currency} size="lg" />
            <Provenance>Набежало за месяц</Provenance>
          </>
        ) : (
          <>
            <Money minor={null} currency={service.currency} size="lg" />
            <Provenance>{BILLING_LABELS[service.billingModel] ?? ''}</Provenance>
          </>
        )}
      </div>

      {/* Тонкая полоса: доля остатка от последнего пополнения. */}
      {service.fillPercent !== null ? (
        <div>
          <div style={{ height: 3, borderRadius: 999, background: 'var(--rose-200)' }}>
            <div
              style={{
                width: `${service.fillPercent}%`,
                height: '100%',
                borderRadius: 999,
                background: tone === 'normal' ? 'var(--burgundy-700)' : 'var(--amber-600)',
                transition: 'width var(--dur-slow) var(--ease-standard)',
              }}
            />
          </div>
          <div style={{ marginTop: 'var(--space-2)' }}>
            <Provenance>
              {service.lastTopUpMinor !== null
                ? `${Math.round(service.fillPercent)}% от пополнения ${formatMoney(service.lastTopUpMinor, service.currency)}`
                : ''}
            </Provenance>
          </div>
        </div>
      ) : null}

      <div
        style={{
          marginTop: 'auto',
          paddingTop: 'var(--space-3)',
          borderTop: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
        }}
      >
        <Provenance>
          {service.billingModel === 'prepaid_balance'
            ? service.lastReconciledAt
              ? `Сверен ${formatDateShort(service.lastReconciledAt)}`
              : 'Не сверялся'
            : service.usageThroughDate
              ? `Данные по ${formatDateShort(service.usageThroughDate)}`
              : 'Данных нет'}
        </Provenance>
        <Button variant="ghost" size="sm" onClick={onOpen}>
          Открыть
        </Button>
      </div>

      {warning ? (
        <div
          style={{
            font: '400 12px/1.45 var(--font-text)',
            color:
              warning.level === 'critical' ? 'var(--status-danger-fg)' : 'var(--status-warning-fg)',
          }}
        >
          {warning.message}
        </div>
      ) : null}
    </Card>
  );
}

function SectionTitle({ children, action }: { children: string; action?: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-4)',
        marginBottom: 'var(--space-5)',
      }}
    >
      <span
        style={{
          font: '500 16px var(--font-display)',
          letterSpacing: 'var(--ls-heading)',
          color: 'var(--text-strong)',
        }}
      >
        {children}
      </span>
      {action}
    </div>
  );
}

export function OverviewScreen() {
  const navigate = useNavigate();
  const overview = useOverview();

  if (overview.isLoading || overview.isError) {
    return (
      <div className="kj-page">
        <QueryState query={overview} what="обзор" />
      </div>
    );
  }

  const data = overview.data;
  if (!data) {
    return (
      <div className="kj-page">
        <EmptyState
          icon="LayoutDashboard"
          title="Обзор не собрался"
          description="Сервер не ответил. Попробуйте обновить страницу."
        />
      </div>
    );
  }

  const today = new Date(Date.now() + 3 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const burning = [...data.burning.overdue, ...data.burning.soon];
  const hasAnything = data.services.length > 0 || data.tasksSync.lastSuccessAt !== null;

  if (!hasAnything) {
    return (
      <div className="kj-page">
        <EmptyState
          icon="LayoutDashboard"
          title="Показывать пока нечего"
          description="Здесь появятся балансы сервисов с прогнозом, просроченные задачи, кто чем занят и свежие уведомления. Всё это появится, как только вы заведёте кошелёк и первый сервис."
          action={
            <Button
              variant="secondary"
              onClick={() => navigate('/settings/services')}
              iconRight={<Icon name="ArrowRight" size={16} />}
            >
              Перейти к сервисам
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="kj-page">
      {/* Блок 1: балансы всех активных сервисов. */}
      {data.services.length > 0 ? (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: 'var(--space-5)',
            alignItems: 'stretch',
          }}
        >
          {data.services.map((service) => (
            <BalanceCard
              key={service.id}
              service={service}
              onOpen={() => navigate(`/finance/${service.id}`)}
            />
          ))}
        </div>
      ) : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 'var(--space-5)',
          alignItems: 'start',
        }}
      >
        {/* Блок 2: что горит. */}
        <Card padding="md">
          <SectionTitle
            action={
              <Button variant="ghost" size="sm" onClick={() => navigate('/tasks')}>
                Все задачи
              </Button>
            }
          >
            Что горит
          </SectionTitle>

          {burning.length === 0 ? (
            <Provenance>
              {data.tasksSync.lastSuccessAt
                ? 'Просроченных задач нет, ближайшие сроки тоже не поджимают'
                : 'Задачи ещё не синхронизированы'}
            </Provenance>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {burning.map((task) => {
                const end = task.dueDateEnd ?? task.dueDate;
                const overdue = end !== null && end < today;
                return (
                  <div
                    key={task.id}
                    style={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 'var(--space-4)',
                      paddingBottom: 'var(--space-3)',
                      borderBottom: '1px solid var(--border-subtle)',
                    }}
                  >
                    <a
                      href={task.notionUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="kj-link-plain"
                      style={{
                        flex: 1,
                        minWidth: 0,
                        font: '400 13px/1.4 var(--font-text)',
                        color: 'var(--text-body)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.title}
                    </a>
                    <span
                      style={{
                        font: '400 12px var(--font-text)',
                        color: 'var(--text-subtle)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {task.assignees.length > 0 ? task.assignees[0]?.name : 'не назначено'}
                    </span>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--fs-2xs)',
                        color: overdue ? 'var(--status-warning-fg)' : 'var(--text-muted)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {end ? formatDateShort(end) : ''}
                    </span>
                  </div>
                );
              })}
              {data.burning.totalOverdue > data.burning.overdue.length ? (
                <Provenance>
                  {`Ещё ${data.burning.totalOverdue - data.burning.overdue.length} просроченных`}
                </Provenance>
              ) : null}
            </div>
          )}
        </Card>

        {/* Блок 3: кто чем занят. */}
        <Card padding="md">
          <SectionTitle>Кто чем занят</SectionTitle>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {data.people.map((person) => (
              <div key={person.userId ?? person.name}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 'var(--space-3)',
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ font: '500 14px var(--font-text)', color: 'var(--text-strong)' }}>
                    {person.name}
                  </span>
                  {person.notionUserId === null ? (
                    <Badge tone="neutral">не сопоставлен с Notion</Badge>
                  ) : null}
                </div>

                {person.inProgress.length === 0 ? (
                  <Provenance>
                    {person.notionUserId === null
                      ? 'Задачи не видны: свяжите с участником Notion'
                      : 'В работе ничего'}
                  </Provenance>
                ) : (
                  <div
                    style={{
                      marginTop: 'var(--space-2)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--space-2)',
                    }}
                  >
                    {person.inProgress.slice(0, 3).map((task) => (
                      <a
                        key={task.id}
                        href={task.notionUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="kj-link-plain"
                        style={{
                          font: '400 13px/1.4 var(--font-text)',
                          color: 'var(--text-muted)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {task.title}
                      </a>
                    ))}
                    {person.inProgress.length > 3 ? (
                      <Provenance>{`и ещё ${person.inProgress.length - 3}`}</Provenance>
                    ) : null}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Блок 4: траты за месяц. */}
      <Card padding="md">
        <SectionTitle
          action={
            <Button variant="ghost" size="sm" onClick={() => navigate('/finance')}>
              Финансы
            </Button>
          }
        >
          Траты за месяц
        </SectionTitle>

        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 'var(--space-4)',
            flexWrap: 'wrap',
            marginBottom: 'var(--space-5)',
          }}
        >
          <Money minor={data.spend.currentMinor} currency={data.spend.currency} size="xl" />
          <Money minor={data.spend.currentUsdMinor} currency="USD" size="md" tone="muted" />
          <span style={{ font: '400 13px var(--font-text)', color: 'var(--text-muted)' }}>
            {data.spend.changePercent === null
              ? 'сравнить не с чем: прошлый месяц пуст'
              : `${formatSignedPercent(data.spend.changePercent)} к прошлому месяцу`}
          </span>
        </div>

        <BarChart
          bars={data.spend.byDay.map((day) => ({
            label: day.date.slice(8, 10),
            value: day.amount,
            caption: formatDate(day.date),
          }))}
          currency={data.spend.currency}
          emptyMessage="В этом месяце расхода ещё не было"
        />

        <div style={{ marginTop: 'var(--space-4)', display: 'grid', gap: 'var(--space-2)' }}>
          {data.spend.other.count > 0 ? (
            <Provenance>
              {`Из них прочие расходы — ${formatMoney(data.spend.other.rubMinor, 'RUB')}, ` +
                `записей: ${data.spend.other.count}`}
            </Provenance>
          ) : null}
          <Provenance>
            {data.spend.previousMinor > 0
              ? `В прошлом месяце ${formatMoney(data.spend.previousMinor, data.spend.currency)}`
              : 'За прошлый месяц данных нет'}
            . Рубли — по курсу пополнений каждого сервиса, без них — по курсу дня.
          </Provenance>
          {data.spend.unratedUsdMinor > 0 ? (
            <Provenance>
              {`Без курса в рубли не вошло ${formatMoney(data.spend.unratedUsdMinor, 'USD')}`}
            </Provenance>
          ) : null}
        </div>
      </Card>

      {/* Блок 5: последние уведомления. */}
      <Card padding="md">
        <SectionTitle
          action={
            <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')}>
              Все уведомления
            </Button>
          }
        >
          Последние уведомления
        </SectionTitle>

        {data.notifications.length === 0 ? (
          <Provenance>
            Мы пишем, когда нужно действие. Пока всё в порядке, здесь тихо.
          </Provenance>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {data.notifications.map((item) => (
              <div key={item.id}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--space-3)' }}>
                  <Eyebrow>{item.level === 'critical' ? 'важно' : item.level}</Eyebrow>
                  <span style={{ font: '500 14px var(--font-text)', color: 'var(--text-strong)' }}>
                    {item.title}
                  </span>
                </div>
                <Provenance>{item.body}</Provenance>
              </div>
            ))}
          </div>
        )}
      </Card>

      {data.services.length > 0 ? (
        <Provenance>
          {`${data.services.length} ${plural(data.services.length, 'активный сервис', 'активных сервиса', 'активных сервисов')}. Расчётный баланс считает приложение: провайдеры остаток по API не отдают`}
        </Provenance>
      ) : null}
    </div>
  );
}
